import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  LoginBody,
  LoginButtonStack,
  LoginErrorText,
  LoginEyebrow,
  LoginFieldGroup,
  LoginFieldLabel,
  LoginFormCard,
  LoginHeroBlock,
  LoginInput,
  LoginPageLayout,
  LoginPanel,
  LoginPrimaryButton,
  LoginSecondaryButton,
  LoginSmallPrint,
  LoginTitle,
} from '../components/styled';
import { useAuth } from '../context/AuthContext';

function getRedirectTarget(locationState) {
  const fromLocation = locationState?.from;

  if (!fromLocation?.pathname || ['/login', '/logout'].includes(fromLocation.pathname.replace(/\/+$/, ''))) {
    return '/library/songs';
  }

  return `${fromLocation.pathname}${fromLocation.search || ''}${fromLocation.hash || ''}`;
}

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isAuthenticated, isReady, login, register, verifyEmail, resendVerification, sessionError,
  } = useAuth();
  const [formState, setFormState] = useState({
    email: '',
    password: '',
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [code, setCode] = useState('');
  const [notice, setNotice] = useState('');
  const [resendAt, setResendAt] = useState(0);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const targetRoute = getRedirectTarget(location.state);

  useEffect(() => {
    const authError = location.state?.authError;
    if (authError) setErrorMessage(authError);
  }, [location.state?.authError]);

  useEffect(() => {
    if (sessionError) setErrorMessage(sessionError);
  }, [sessionError]);

  useEffect(() => {
    const updateCountdown = () => {
      setSecondsRemaining(Math.max(0, Math.ceil((resendAt - Date.now()) / 1000)));
    };
    updateCountdown();
    const timer = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(timer);
  }, [resendAt]);

  if (!isReady) {
    return null;
  }

  if (isAuthenticated) {
    return <Navigate to={targetRoute} replace />;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormState((currentState) => ({
      ...currentState,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    setNotice('');

    try {
      if (isVerifying) {
        await verifyEmail({ ...formState, code });
        setIsVerifying(false);
        setIsRegisterMode(false);
        setCode('');
        setNotice('Email verified. Your account is ready.');
      } else if (isRegisterMode) {
        const data = await register(formState);
        setIsVerifying(true);
        setFormState((current) => ({ ...current, email: data.email }));
        setNotice(data.message);
        setResendAt(Date.now() + (data.retryAfter || 60) * 1000);
        return;
      }

      await login(formState);
      navigate(targetRoute);
    } catch (error) {
      setErrorMessage(error.message);
      if (error.data?.verificationRequired) {
        setIsVerifying(true);
        setCode('');
        if (error.data.email) {
          setFormState((current) => ({ ...current, email: error.data.email }));
        }
        setResendAt(Date.now() + (error.data.retryAfter || 0) * 1000);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModeToggle = () => {
    setErrorMessage('');
    setNotice('');
    setCode('');
    if (isVerifying) {
      setIsVerifying(false);
      setIsRegisterMode(false);
    } else {
      setIsRegisterMode((currentValue) => !currentValue);
    }
  };

  const handleResend = async () => {
    setIsSubmitting(true);
    setErrorMessage('');
    setNotice('');
    try {
      const data = await resendVerification(formState);
      setNotice(data.message);
      setCode('');
      setResendAt(Date.now() + (data.retryAfter || 60) * 1000);
    } catch (error) {
      setErrorMessage(error.message);
      if (error.data?.retryAfter) {
        setResendAt(Date.now() + error.data.retryAfter * 1000);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LoginPageLayout>
      <LoginPanel>
        <LoginHeroBlock>
          <LoginEyebrow>Tab &amp; Chords</LoginEyebrow>
          <LoginTitle>
            {isVerifying
              ? 'Verify your email.'
              : isRegisterMode
                ? 'Create your Tab & Chords account.'
                : 'Sign in to your song library.'}
          </LoginTitle>
          <LoginBody>
            {isVerifying ? formState.email : 'Your songs, chords, and tabs in one place.'}
          </LoginBody>
        </LoginHeroBlock>

        <LoginFormCard onSubmit={handleSubmit}>
          {isVerifying ? (
            <LoginFieldGroup>
              <LoginFieldLabel htmlFor="verification-code">Email verification code</LoginFieldLabel>
              <LoginInput
                id="verification-code"
                name="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                minLength={6}
                maxLength={6}
                required
                autoFocus
                disabled={isSubmitting}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
              />
            </LoginFieldGroup>
          ) : (
            <>
              <LoginFieldGroup>
                <LoginFieldLabel htmlFor="email">Email</LoginFieldLabel>
                <LoginInput
                  id="email"
                  name="email"
                  type="email"
                  required
                  disabled={isSubmitting}
                  autoComplete="email"
                  value={formState.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                />
              </LoginFieldGroup>

              <LoginFieldGroup>
                <LoginFieldLabel htmlFor="password">Password</LoginFieldLabel>
                <LoginInput
                  id="password"
                  name="password"
                  type="password"
                  required
                  disabled={isSubmitting}
                  autoComplete={isRegisterMode ? 'new-password' : 'current-password'}
                  value={formState.password}
                  onChange={handleChange}
                  placeholder={isRegisterMode ? 'Create password' : 'Enter password'}
                />
              </LoginFieldGroup>
            </>
          )}

          {errorMessage ? <LoginErrorText role="alert">{errorMessage}</LoginErrorText> : null}
          {notice ? <LoginSmallPrint role="status">{notice}</LoginSmallPrint> : null}

          <LoginButtonStack>
            <LoginPrimaryButton type="submit" disabled={isSubmitting}>
              {isVerifying
                ? (isSubmitting ? 'Please Wait...' : 'Verify Email')
                : isRegisterMode
                  ? (isSubmitting ? 'Creating Account...' : 'Create Account')
                  : (isSubmitting ? 'Signing In...' : 'Sign In')}
            </LoginPrimaryButton>
            {isVerifying ? (
              <LoginSecondaryButton
                type="button"
                onClick={handleResend}
                disabled={isSubmitting || secondsRemaining > 0}
              >
                {secondsRemaining > 0 ? `Resend Code (${secondsRemaining}s)` : 'Resend Code'}
              </LoginSecondaryButton>
            ) : null}
            <LoginSecondaryButton
              type="button"
              onClick={handleModeToggle}
              disabled={isSubmitting}
            >
              {isVerifying || isRegisterMode ? 'Back to Sign In' : 'Need an Account?'}
            </LoginSecondaryButton>
          </LoginButtonStack>

          {isVerifying ? <LoginSmallPrint>Codes expire after 10 minutes.</LoginSmallPrint> : null}
        </LoginFormCard>
      </LoginPanel>
    </LoginPageLayout>
  );
}

export default LoginPage;
